import { isAxiosError } from "axios";

import type { AssessmentQuestionResponseFormat } from "@/types/screener-builder/questions";
import type { QuestionWarning } from "@/types/screener-builder";
import type { AxiosErrorWithResponse } from "@/types/third-party";
import type { Nullable } from "@/types/utils";

export enum AssessmentQuestionFields {
  Competency = "competencyName",
  Definition = "competencyDefinition",
  Question = "question",
}

export interface FieldValidationState {
  error: Nullable<string>;
  valid: boolean;
  validationErrors: string[];
  warning?: QuestionWarning;
}

export interface GenerateButtonState<Field extends AssessmentQuestionFields> {
  disabled: boolean;
  generateInput: Nullable<GenerateInputsMap[Field]>;
  tooltip: string;
  isRegenerate: boolean;
}

interface CompetencyNameFromDefinition {
  competencyDefinition: string;
  question?: string;
}
interface CompetencyNameFromQuestion {
  competencyDefinition?: string;
  question: string;
}
type CompetencyNameGenerationInput =
  | CompetencyNameFromDefinition
  | CompetencyNameFromQuestion;

interface CompetencyDefinitionGenerationInput {
  competencyName: string;
  question?: string;
}

interface CompetencyQuestionFromName {
  competencyDefinition?: string;
  competencyName: string;
}
interface CompetencyQuestionFromDefinition {
  competencyDefinition: string;
  competencyName?: string;
}
type CompetencyQuestionGenerationInput =
  | CompetencyQuestionFromName
  | CompetencyQuestionFromDefinition;

interface ScoringRubricGenerationInput {
  competencyDefinition: string;
  competencyName: string;
  question: string;
}

export const SCORING_RUBRIC = "scoringRubric" as const;

export type GeneratableFields =
  | AssessmentQuestionFields
  | typeof SCORING_RUBRIC;

interface BaseGenerationRequirements {
  jobDescription?: string;
  jobTitle: string;
}

export type WithBaseGenerationRequirements<T extends object> =
  BaseGenerationRequirements & T;

export interface GenerateInputsMap {
  [AssessmentQuestionFields.Competency]: CompetencyNameGenerationInput;
  [AssessmentQuestionFields.Definition]: CompetencyDefinitionGenerationInput;
  [AssessmentQuestionFields.Question]: CompetencyQuestionGenerationInput;
  [SCORING_RUBRIC]: ScoringRubricGenerationInput;
}

export interface GenerateCompetencyFieldBody<T extends GeneratableFields> {
  prompt: T;
  components: WithBaseGenerationRequirements<GenerateInputsMap[T]>;
  priorOutput?: string;
  errors?: string[];
}

interface ScoringRubricResponse {
  high: string;
  medium: string;
  low: string;
}

interface QuestionResponse {
  id: string;
  is_selected: boolean;
  question: string;
}

interface GenerateResponseWithContent<T> {
  content: T;
}

type GenerateResponseMap = {
  [AssessmentQuestionFields.Competency]: GenerateResponseWithContent<string>;
  [AssessmentQuestionFields.Definition]: GenerateResponseWithContent<string>;
  [AssessmentQuestionFields.Question]: GenerateResponseWithContent<
    QuestionResponse[]
  >;
  [SCORING_RUBRIC]: GenerateResponseWithContent<ScoringRubricResponse>;
};

export type GenerateResponse<T extends GeneratableFields> =
  GenerateResponseMap[T];

// Warnings are returned on a 422 response. They indicate that the LLM found
// a problem with the inputs (EEOC, inconsistency, nonsensical data, etc.)
export interface GenerateValidationError {
  message: string;
  warnings: string[];
}

export const isGenerateValidationError = (
  error: unknown,
): error is AxiosErrorWithResponse<GenerateValidationError> =>
  isAxiosError(error) &&
  error.status === 422 &&
  !!error.response?.data.warnings;

export enum SecondaryCompetencyModalLoadingFields {
  Rubric = "scoringRubric",
  Validation = "validation",
}

export type CompetencyModalLoadingFields =
  | SecondaryCompetencyModalLoadingFields
  | AssessmentQuestionFields;

export interface QuestionOption {
  questionId: string;
  question: string;
  isIncluded?: boolean;
  order?: number;
}

export interface SaveCompetencyPayload {
  id?: string;
  screenerId: string;
  competencyName: string;
  displayName?: string;
  competencyDefinition: string;
  question:
    | string
    // questionId will be defined on the payload for existing questions only
    | (Omit<QuestionOption, "questionId"> & { questionId?: string })[];
  isCustomQuestion: boolean;
  highScoringAnswerDescription: string;
  mediumScoringAnswerDescription: string;
  lowScoringAnswerDescription: string;
  isCustomScoringRubric: boolean;
  recordingDurationSeconds?: number;
  responseFormat?: AssessmentQuestionResponseFormat;
  canSkip?: boolean;
  warnings?: string;
}

export interface SaveCompetencyResponse {
  canSkip?: boolean;
  competencyDefinition: string;
  competencyName: string;
  displayName: string;
  highScoringAnswerDescription: string;
  id: string;
  isCustomQuestion: boolean;
  isCustomScoringRubric: boolean;
  lowScoringAnswerDescription: string;
  mediumScoringAnswerDescription: string;
  question: QuestionOption | QuestionOption[];
  recordingDurationSeconds?: number;
  responseFormat?: AssessmentQuestionResponseFormat;
  screenerId: string;
  warnings: string | null;
}
